import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Bell, BookOpen, BookUser, Bot, Briefcase, Clapperboard, FileText, Flame, Globe2, Handshake, Headset, KeyRound, LayoutDashboard, LifeBuoy, LogOut, Menu, MessageCircle, MessagesSquare, Newspaper, Package, PackageX, Scale, Search, Settings, ShoppingBag, Store, TrendingUp, Truck, Users, Wallet, X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { Brand } from "../../components/Navbar";
import ChangePassword from "../../components/ChangePassword";
import api from "../../lib/api";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "admin-sidebar-dashboard-link" },
  { to: "/admin/ai", label: "AI Assistant", icon: Bot, end: true, testid: "admin-sidebar-ai-link" },
  { to: "/admin/ai/chats", label: "AI Chats", icon: MessagesSquare, testid: "admin-sidebar-aichats-link" },
  { to: "/admin/ai/leads", label: "AI Leads", icon: Flame, testid: "admin-sidebar-aileads-link" },
  { to: "/admin/ai/knowledge", label: "AI Knowledge Base", icon: BookOpen, testid: "admin-sidebar-aikb-link" },
  { to: "/admin/ai/settings", label: "AI Settings", icon: Settings, testid: "admin-sidebar-aisettings-link" },
  { to: "/admin/shipments", label: "Shipments", icon: Package, testid: "admin-sidebar-shipments-link" },
  { to: "/admin/ndr", label: "NDR / RTO", icon: PackageX, testid: "admin-sidebar-ndr-link" },
  { to: "/admin/shop", label: "Shop", icon: ShoppingBag, testid: "admin-sidebar-shop-link" },
  { to: "/admin/media", label: "Media & Videos", icon: Clapperboard, testid: "admin-sidebar-media-link" },
  { to: "/admin/branches", label: "Branches", icon: Store, testid: "admin-sidebar-branches-link" },
  { to: "/admin/partner-enquiries", label: "Partner Enquiries", icon: Handshake, testid: "admin-sidebar-enquiries-link" },
  { to: "/admin/careers", label: "Careers", icon: Briefcase, testid: "admin-sidebar-careers-link" },
  { to: "/admin/legal", label: "Legal Pages", icon: Scale, testid: "admin-sidebar-legal-link" },
  { to: "/admin/faqs", label: "FAQs", icon: MessageCircle, testid: "admin-sidebar-faqs-link" },
  { to: "/admin/news", label: "News & Blog", icon: Newspaper, testid: "admin-sidebar-news-link" },
  { to: "/admin/customers", label: "Customers & Partners", icon: Users, testid: "admin-sidebar-customers-link" },
  { to: "/admin/commissions", label: "Rates & Commissions", icon: Wallet, testid: "admin-sidebar-commission-link" },
  { to: "/admin/intl-rates", label: "Intl. Rate Engine", icon: Globe2, testid: "admin-sidebar-intlrates-link" },
  { to: "/admin/api-access", label: "API Access", icon: KeyRound, testid: "admin-sidebar-apiaccess-link" },
  { to: "/admin/profit", label: "Profit & Expenses", icon: TrendingUp, testid: "admin-sidebar-profit-link" },
  { to: "/admin/contacts", label: "Contacts DB", icon: BookUser, testid: "admin-sidebar-contacts-link" },
  { to: "/admin/crm", label: "CRM & Pickups", icon: Headset, testid: "admin-sidebar-crm-link" },
  { to: "/admin/support", label: "Support & Claims", icon: LifeBuoy, testid: "admin-sidebar-support-link" },
  { to: "/admin/couriers", label: "Courier Partners", icon: Truck, testid: "admin-sidebar-couriers-link" },
  { to: "/admin/cms", label: "Website CMS", icon: Settings, testid: "admin-sidebar-cms-link" },
  { to: "/admin/audit", label: "Audit Logs", icon: FileText, testid: "admin-sidebar-audit-link" },
];

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const navigate = useNavigate();
  const timer = useRef(null);

  const onChange = (e) => {
    const v = e.target.value;
    setQ(v);
    clearTimeout(timer.current);
    if (v.length < 2) { setResults(null); return; }
    timer.current = setTimeout(async () => {
      try { setResults((await api.get(`/admin/search?q=${encodeURIComponent(v)}`)).data); } catch { /* noop */ }
    }, 300);
  };

  return (
    <div className="relative w-full min-w-0 max-w-md">
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
      <input value={q} onChange={onChange} data-testid="admin-global-search"
             placeholder="Search AWB, order, customer, phone…"
             className="w-full rounded-lg border border-adminborder bg-admincard py-2 pl-9 pr-3 text-sm text-slate-200 placeholder-slate-500 outline-none focus:border-saffron" />
      {results && (
        <div className="absolute left-0 right-0 top-11 z-50 max-h-80 overflow-auto rounded-xl border border-adminborder bg-admincard p-2 shadow-2xl" data-testid="admin-search-results">
          {results.shipments.map((s) => (
            <button key={s.id} onClick={() => { navigate("/admin/shipments"); setResults(null); setQ(""); }}
                    className="block w-full rounded-lg px-3 py-2 text-left text-sm text-slate-200 hover:bg-white/5">
              <span className="font-mono text-saffron">{s.awb}</span> · {s.customer_name} · <span className="text-slate-400">{s.status}</span>
            </button>
          ))}
          {results.users.map((u) => (
            <button key={u.id} onClick={() => { navigate("/admin/customers"); setResults(null); setQ(""); }}
                    className="block w-full rounded-lg px-3 py-2 text-left text-sm text-slate-200 hover:bg-white/5">
              {u.name} · <span className="text-slate-400">{u.email} · {u.role}</span>
            </button>
          ))}
          {!results.shipments.length && !results.users.length && <p className="px-3 py-2 text-sm text-slate-500">No results</p>}
        </div>
      )}
    </div>
  );
}

function NdrBell() {
  const [alerts, setAlerts] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/ndr/alerts").then((r) => setAlerts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const markRead = async () => {
    try { await api.post("/ndr/alerts/read"); load(); } catch { /* noop */ }
  };
  const waLink = (a) => {
    const digits = String(alerts?.admin_phone || "").replace(/\D/g, "");
    if (digits.length < 10) return null;
    const num = digits.length === 10 ? `91${digits}` : digits;
    const text = encodeURIComponent(`NDR ALERT ${a.awb} (${a.order_id || ""}) — ${a.receiver_name || ""}, ${a.city || ""}. Reason: ${a.reason || "delivery failed"}. Act within 24h to avoid RTO.`);
    return `https://wa.me/${num}?text=${text}`;
  };
  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} data-testid="ndr-bell-btn" aria-label="NDR alerts"
              className="relative rounded-lg border border-adminborder p-2 text-slate-300 transition-colors hover:border-saffron hover:text-saffron">
        <Bell className="h-5 w-5" />
        {alerts?.unread > 0 && (
          <span data-testid="ndr-bell-badge"
                className="absolute -right-1.5 -top-1.5 grid h-5 min-w-5 place-items-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
            {alerts.unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-12 z-50 w-96 max-w-[90vw] rounded-xl border border-adminborder bg-admincard p-3 shadow-2xl" data-testid="ndr-bell-dropdown">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-bold text-white">NDR Alerts</p>
            {alerts?.unread > 0 && (
              <button onClick={markRead} data-testid="ndr-bell-read-btn" className="text-xs font-semibold text-saffron hover:underline">Mark all read</button>
            )}
          </div>
          <div className="max-h-80 space-y-2 overflow-y-auto">
            {(alerts?.items || []).length === 0 && (
              <p className="py-6 text-center text-xs text-slate-500" data-testid="ndr-bell-empty">No NDR alerts. Daily 9 AM sync will notify here.</p>
            )}
            {(alerts?.items || []).map((a) => (
              <div key={a.id} className={`rounded-lg p-3 ${a.read ? "bg-white/[0.03]" : "bg-saffron/10 ring-1 ring-saffron/30"}`} data-testid={`ndr-alert-${a.awb}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs font-bold text-saffron">{a.awb}</span>
                  <span className="text-[10px] text-slate-500">{(a.created_at || "").slice(0, 10)}</span>
                </div>
                <p className="mt-1 text-xs text-slate-300">{a.receiver_name} · {a.city}</p>
                <p className="mt-0.5 text-[11px] text-slate-500">{a.reason}</p>
                <div className="mt-2 flex items-center gap-2">
                  <NavLink to="/admin/ndr" onClick={() => setOpen(false)} data-testid={`ndr-alert-open-${a.awb}`}
                           className="text-[11px] font-bold text-saffron hover:underline">Open NDR panel</NavLink>
                  {waLink(a) && (
                    <a href={waLink(a)} target="_blank" rel="noreferrer" data-testid={`ndr-alert-wa-${a.awb}`}
                       className="flex items-center gap-1 text-[11px] font-bold text-emerald-400 hover:underline">
                      <MessageCircle className="h-3 w-3" /> Send to my WhatsApp
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div className="flex min-h-screen bg-adminbg text-slate-200">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-60 flex-col border-r border-adminborder bg-adminbg grain lg:flex">
        <div className="flex h-16 items-center border-b border-adminborder px-5"><Brand dark /></div>
        <nav className="flex-1 space-y-1 overflow-y-auto p-3">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testid}
                     className={({ isActive }) =>
                       `flex items-center gap-3 rounded-lg px-3.5 py-2.5 text-sm font-semibold transition-colors ${
                         isActive ? "bg-saffron/15 text-saffron" : "text-slate-400 hover:bg-white/5 hover:text-white"}`}>
              <n.icon className="h-4 w-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-adminborder p-4">
          <p className="truncate text-sm font-semibold text-white">{user?.name}</p>
          <p className="text-xs capitalize text-slate-500">{user?.role?.replace("_", " ")}</p>
          <ChangePassword testid="admin-change-password-btn"
                          triggerClass="mt-3 flex items-center gap-2 text-xs font-semibold text-slate-400 hover:text-saffron" />
          <button onClick={async () => { await logout(); navigate("/"); }} data-testid="admin-logout-btn"
                  className="mt-3 flex items-center gap-2 text-xs font-semibold text-slate-400 hover:text-saffron">
            <LogOut className="h-3.5 w-3.5" /> Logout
          </button>
        </div>
      </aside>

      <div className="flex-1 lg:pl-60">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between gap-4 border-b border-adminborder bg-adminbg/90 px-4 backdrop-blur-xl sm:px-6">
          <div className="flex items-center gap-3 lg:hidden">
            <button onClick={() => setMenuOpen(true)} data-testid="admin-mobile-menu-btn" aria-label="Open menu"
                    className="rounded-lg border border-adminborder p-2 text-slate-300 hover:border-saffron hover:text-saffron">
              <Menu className="h-5 w-5" />
            </button>
            <Brand dark />
          </div>
          <GlobalSearch />
          <NdrBell />
        </header>
        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>

      {menuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="admin-mobile-menu">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMenuOpen(false)} />
          <div className="absolute inset-y-0 left-0 flex w-72 flex-col border-r border-adminborder bg-adminbg">
            <div className="flex h-16 items-center justify-between border-b border-adminborder px-5">
              <Brand dark />
              <button onClick={() => setMenuOpen(false)} data-testid="admin-mobile-menu-close" aria-label="Close menu"
                      className="rounded-lg p-2 text-slate-400 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex-1 space-y-1 overflow-y-auto p-3">
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setMenuOpen(false)} data-testid={`${n.testid}-mobile`}
                         className={({ isActive }) =>
                           `flex items-center gap-3 rounded-lg px-3.5 py-2.5 text-sm font-semibold transition-colors ${
                             isActive ? "bg-saffron/15 text-saffron" : "text-slate-400 hover:bg-white/5 hover:text-white"}`}>
                  <n.icon className="h-4 w-4" /> {n.label}
                </NavLink>
              ))}
            </nav>
            <div className="border-t border-adminborder p-4">
              <p className="truncate text-sm font-semibold text-white">{user?.name}</p>
              <p className="text-xs capitalize text-slate-500">{user?.role?.replace("_", " ")}</p>
              <ChangePassword testid="admin-mobile-change-password-btn"
                              triggerClass="mt-3 flex items-center gap-2 text-xs font-semibold text-slate-400 hover:text-saffron" />
              <button onClick={async () => { await logout(); navigate("/"); }} data-testid="admin-mobile-logout-btn"
                      className="mt-3 flex items-center gap-2 text-xs font-semibold text-slate-400 hover:text-saffron">
                <LogOut className="h-3.5 w-3.5" /> Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
